import React, { useMemo, useState } from "react";
import QRCode from "qrcode";
import * as XLSX from "xlsx";

interface LabelData {
  code: string;
  displayNumber: string;
  qrDataUrl: string;
}

interface SheetLabelGroup {
  sheetName: string;
  codeColumn: string;
  labels: LabelData[];
}

const PREVIEW_LIMIT = 12;
const BATCH_SIZE = 100;
const LABEL_BACKGROUND = "/images/04-27068.png";

const styles = `
.label-printing-container {
  padding: 20px;
  background: #f5f5f5;
  min-height: 100vh;
}

.controls {
  background: white;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.controls h2 {
  margin: 0 0 20px 0;
  color: #333;
  font-size: 24px;
}

.control-row {
  display: flex;
  gap: 15px;
  align-items: flex-end;
  flex-wrap: wrap;
}

.control-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.control-group label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.control-group input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-width: 320px;
}

.btn-generate,
.btn-print {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-generate {
  background: #4CAF50;
  color: white;
}

.btn-generate:hover {
  background: #45a049;
}

.btn-print {
  background: #2196F3;
  color: white;
}

.btn-print:hover:not(:disabled) {
  background: #0b7dda;
}

.btn-print:disabled,
.btn-generate:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.info {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #eee;
}

.info p {
  margin: 5px 0;
  color: #666;
  font-size: 14px;
}

.error-text {
  color: #c62828;
  font-weight: 600;
}

.print-area {
  background: white;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.page-container {
  width: 320mm;
  min-height: 430mm;
  margin: 0 auto;
  padding: 10mm;
  background: white;
  display: flex;
  flex-wrap: wrap;
  gap: 5mm;
  align-content: flex-start;
}

.label-card {
  width: 90mm;
  height: 54mm;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  overflow: hidden;
  padding: 3mm;
  border: 2px solid #000;
  border-radius: 8mm;
  background-image: url(${LABEL_BACKGROUND});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
  color-adjust: exact;
}

.qr-overlay {
  position: absolute;
  left: 10mm;
  top: 50%;
  transform: translateY(-50%);
  width: 26mm;
  height: 26mm;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 25px;
}

.qr-overlay img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.code-section {
  position: absolute;
  right: 12mm;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 25px;
}

.code-number {
  font-size: 24px;
  font-family: Arial, Helvetica, sans-serif;
  font-weight: 700;
  color: #006f83;
  letter-spacing: 0;
  line-height: 1;
  white-space: nowrap;
}

@media print {
  body {
    margin: 0;
    padding: 0;
  }

  .label-printing-container {
    padding: 0;
    background: white;
  }

  .no-print {
    display: none !important;
  }

  .print-area {
    padding: 0;
    box-shadow: none;
    border-radius: 0;
  }

  .page-container {
    width: 320mm;
    height: 430mm;
    margin: 0;
    padding: 10mm;
    page-break-after: always;
  }

  .label-card {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  @page {
    size: 320mm 430mm;
    margin: 0;
  }
}

@media screen and (max-width: 1400px) {
  .page-container {
    transform: scale(0.8);
    transform-origin: top center;
  }
}

@media screen and (max-width: 1100px) {
  .page-container {
    transform: scale(0.6);
    transform-origin: top center;
  }
}
`;

const normalizeHeader = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/\s+/g, " ");

const isAssetCode = (value: unknown) =>
  /^(\d{2}-\d{4,}|[A-Z]{2}\.\d{3}\.\d{4,})$/i.test(
    String(value ?? "").trim(),
  );

const findCodeColumnIndex = (headers: unknown[]) => {
  const normalizedHeaders = headers.map(normalizeHeader);
  const preferredHeaders = [
    "ma ipad",
    "ma ccdc",
    "ma tai san",
    "ma",
    "code",
  ];

  const preferredIndex = normalizedHeaders.findIndex((header) =>
    preferredHeaders.some((candidate) => header === candidate),
  );

  if (preferredIndex >= 0) return preferredIndex;

  return normalizedHeaders.findIndex(
    (header) =>
      !header.startsWith("bang ke") &&
      (header.includes("ma ") ||
        header.includes("code") ||
        header.includes("ccdc") ||
        header.includes("ipad")),
  );
};

const findHeaderRow = (rows: unknown[][]) => {
  for (let index = 0; index < Math.min(rows.length, 20); index += 1) {
    const codeColumnIndex = findCodeColumnIndex(rows[index]);
    const sampleValues = rows
      .slice(index + 1, index + 8)
      .map((row) => row?.[codeColumnIndex])
      .filter((value) => String(value ?? "").trim().length > 0);

    if (
      codeColumnIndex >= 0 &&
      sampleValues.some((value) => isAssetCode(value))
    ) {
      return { headerRowIndex: index, codeColumnIndex };
    }
  }

  return { headerRowIndex: -1, codeColumnIndex: -1 };
};

const getCellValue = (row: unknown[], index: number) =>
  String(row[index] ?? "").trim();

const buildLabels = async (codes: string[]) => {
  const labels: LabelData[] = [];

  for (const code of codes) {
    const qrDataUrl = await QRCode.toDataURL(code, {
      width: 200,
      margin: 0,
      errorCorrectionLevel: "H",
      color: {
        dark: "#000000",
        light: "#FFFFFF",
      },
    });

    labels.push({
      code,
      displayNumber: code,
      qrDataUrl,
    });
  }

  return labels;
};

const renderLabel = (label: LabelData) => (
  <div className="label-card">
    <div className="qr-overlay">
      <img src={label.qrDataUrl} alt={`QR ${label.code}`} />
    </div>
    <div className="code-section">
      <div className="code-number">{label.displayNumber}</div>
    </div>
  </div>
);

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const LabelPrinting: React.FC = () => {
  const [sheetGroups, setSheetGroups] = useState<SheetLabelGroup[]>([]);
  const [fileName, setFileName] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const totalLabels = useMemo(
    () => sheetGroups.reduce((total, group) => total + group.labels.length, 0),
    [sheetGroups],
  );

  const parseWorkbook = async (workbook: XLSX.WorkBook) => {
    const targetSheetNames = workbook.SheetNames.slice(0, 2);
    const groups: SheetLabelGroup[] = [];

    for (const sheetName of targetSheetNames) {
      const sheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
        header: 1,
        defval: "",
        raw: false,
      });
      const { headerRowIndex, codeColumnIndex } = findHeaderRow(rows);

      if (headerRowIndex < 0 || codeColumnIndex < 0) {
        throw new Error(`Khong tim thay cot ma o sheet ${sheetName}`);
      }

      const codeColumn = getCellValue(rows[headerRowIndex], codeColumnIndex);
      const codes = rows
        .slice(headerRowIndex + 1)
        .map((row) => getCellValue(row, codeColumnIndex))
        .filter((code) => code.length > 0);

      groups.push({
        sheetName,
        codeColumn,
        labels: await buildLabels(codes),
      });
    }

    setSheetGroups(groups);
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setErrorMessage("");
    setIsGenerating(true);

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      await parseWorkbook(workbook);
    } catch (error: any) {
      console.error(error);
      setSheetGroups([]);
      setErrorMessage(error?.message || "Khong doc duoc file Excel");
    } finally {
      setIsGenerating(false);
      event.target.value = "";
    }
  };

  const handlePrintBatch = (
    labels: LabelData[],
    batchNumber: number,
    title: string,
  ) => {
    const startIdx = (batchNumber - 1) * BATCH_SIZE;
    const endIdx = Math.min(startIdx + BATCH_SIZE, labels.length);
    const batchLabels = labels.slice(startIdx, endIdx);

    const labelHtml = batchLabels
      .map(
        (label) => `
      <div class="label-card">
        <div class="qr-overlay">
          <img src="${label.qrDataUrl}" alt="QR ${escapeHtml(label.code)}" />
        </div>
        <div class="code-section">
          <div class="code-number">${escapeHtml(label.displayNumber)}</div>
        </div>
      </div>
    `,
      )
      .join("");

    const popupContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${escapeHtml(title)} - Dot ${batchNumber} (${startIdx + 1}-${endIdx})</title>
        <style>
          ${styles}
          body { margin: 0; padding: 0; background: white; }
          .page-container {
            width: 320mm;
            min-height: 430mm;
            margin: 0 auto;
            padding: 10mm;
            background: white;
            display: flex;
            flex-wrap: wrap;
            gap: 5mm;
            align-content: flex-start;
          }
        </style>
      </head>
      <body>
        <div class="page-container">
          ${labelHtml}
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open("", "_blank", "width=1200,height=900");
    if (printWindow) {
      printWindow.document.write(popupContent);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
        printWindow.onafterprint = () => printWindow.close();
      };
    }
  };

  const renderBatchButtons = (group: SheetLabelGroup) => (
    <div
      style={{
        marginTop: "15px",
        display: "flex",
        flexWrap: "wrap",
        gap: "10px",
      }}
    >
      <p
        style={{
          width: "100%",
          margin: "0 0 10px 0",
          fontWeight: "bold",
          color: "#333",
        }}
      >
        In theo dot, moi dot {BATCH_SIZE} tem:
      </p>
      {Array.from(
        { length: Math.ceil(group.labels.length / BATCH_SIZE) },
        (_, index) => {
          const batchNumber = index + 1;
          const start = index * BATCH_SIZE + 1;
          const end = Math.min((index + 1) * BATCH_SIZE, group.labels.length);

          return (
            <button
              key={batchNumber}
              onClick={() =>
                handlePrintBatch(group.labels, batchNumber, group.sheetName)
              }
              className="btn-print"
              style={{ minWidth: "120px" }}
            >
              Dot {batchNumber} ({start}-{end})
            </button>
          );
        },
      )}
    </div>
  );

  return (
    <>
      <style>{styles}</style>
      <div className="label-printing-container">
        <div className="controls no-print">
          <h2>In tem theo file Excel</h2>
          <div className="control-row">
            <div className="control-group">
              <label>File Excel</label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                disabled={isGenerating}
              />
            </div>
            <button className="btn-generate" disabled={isGenerating}>
              {isGenerating ? "Dang tao QR..." : "Chon file de tao tem"}
            </button>
          </div>
          <div className="info">
            <p>File: {fileName || "Chua chon file"}</p>
            <p>Doc 2 sheet dau tien trong file.</p>
            <p>Tu dong lay cot ma: Ma IPAD, Ma CCDC, Ma tai san hoac Code.</p>
            <p>Kich thuoc tem: 90mm x 54mm</p>
            <p>Khung in: 320mm x 430mm</p>
            <p>Tong so tem: {totalLabels}</p>
            {errorMessage && <p className="error-text">{errorMessage}</p>}
          </div>
        </div>

        {sheetGroups.map((group) => (
          <div className="controls no-print" key={group.sheetName}>
            <h2>
              {group.sheetName} ({group.labels.length} tem)
            </h2>
            <div className="info">
              <p>Cot ma dang dung: {group.codeColumn}</p>
              <p>
                Ma dau: {group.labels[0]?.displayNumber || ""} - Ma cuoi:{" "}
                {group.labels[group.labels.length - 1]?.displayNumber || ""}
              </p>
            </div>
            {group.labels.length > 0 && renderBatchButtons(group)}
          </div>
        ))}

        {sheetGroups.map((group) => (
          <div className="print-area" key={`${group.sheetName}-preview`}>
            <h3 className="no-print" style={{ marginBottom: "10px" }}>
              {group.sheetName}
            </h3>
            {group.labels.length > PREVIEW_LIMIT && (
              <p
                className="no-print"
                style={{
                  color: "#ff9800",
                  fontWeight: "bold",
                  marginBottom: "10px",
                }}
              >
                Hien thi {PREVIEW_LIMIT} tem dau tien de xem truoc. Su dung nut
                in theo dot o tren de in day du.
              </p>
            )}
            <div className="page-container">
              {group.labels.slice(0, PREVIEW_LIMIT).map((label) => (
                <React.Fragment key={label.code}>
                  {renderLabel(label)}
                </React.Fragment>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default LabelPrinting;
